import { Section } from '@/components/layout';
import { Reveal } from '@/components/motion';
import { TicketCta } from '@/components/ui';
import {
  formatBlockTime,
  formatDateRangeLong,
  formatDayLabel,
  formatLongDate,
  type ProgramDay,
  type TourEvent,
} from '@/features/events';
import { ResponsiveImage } from '@/components/media';
import { cx } from '@/lib/cx';
import { CorazonContorno, IconLuna, IconSol } from '../icons/BogotaIcons';
import styles from './LowerGridBogota.module.css';

/* =============================================================================
 * S4 · ZONA BAJA DE 3 COLUMNAS  (mockup y 1048–1375 · 68,2–89,5 % del alto)
 * -----------------------------------------------------------------------------
 * CRONOGRAMA · AZRAHEL · ENTRADAS, separadas por filetes dorados verticales.
 * Cada columna abre con el mismo patrón: filete — TÍTULO — filete, y un corazón
 * de contorno debajo.
 *
 * ── DESVIACIÓN DELIBERADA, LA MÁS IMPORTANTE DE ESTA PÁGINA ──────────────────
 * El mockup rotula un cronograma INVENTADO por el generador de imagen:
 *   «Apertura y alineación grupal / Sanación de la consciencia /
 *    Activación del linaje estelar / Cámara Cuántica» (día 1)
 *   «Frecuencia Origen / Integración y expansión / Ceremonia de cierre /
 *    Anclaje de tu nueva frecuencia» (día 2)
 * El programa REAL del cliente (docs/brief/02 §1 Evento 2/5, ya tipado en
 * `src/features/events`) es otro: «Liberación del Alma — Los 1.000 Sueños del Yo
 * Perdido», «Año 2040, Tiempo del Fin», «Encuentro Estelar en la Cámara Cuántica»
 * el sábado, y el taller de El Umbral de Orión el domingo (`TALLER_DOMINGO`,
 * el mismo en las cuatro conferencias desde el 30-jul-2026).
 * MANDA EL DATO, no el PNG. Es el mismo fallo que FORGE-ECHO cazó en Cancún.
 * Además se rotulan las HORAS reales, que el mockup no trae. Registrado en
 * `docs/VISUAL_DEVIATIONS.md`.
 *
 * ── ZONA DE HONESTIDAD (ADR-003 §2/§3) ───────────────────────────────────────
 * La columna ENTRADAS no lleva precio, ni aforo, ni «plazas restantes». El CTA
 * lo resuelve `TicketCta` a partir de `event.salesState`: hoy `coming-soon`, así
 * que es un `<button aria-disabled>` con el estado leído por el lector de
 * pantalla. Jamás un `href` roto ni un `#`.
 * ========================================================================== */

/**
 * `sobreFoto` NO es un adorno: es un requisito de accesibilidad.
 *
 * Las columnas CRONOGRAMA y AZRAHEL abren sobre el crema de la sección, donde
 * `--az-gold-sm` da 5,06:1 y cumple AA de sobra. ENTRADAS abre sobre la FOTO
 * del cielo de la tarjeta, y ahí el mismo token medía **2,33:1** (medido píxel
 * a píxel bajo los glifos, a 1440 · 1024 · 390). Contra una foto no hay color
 * que salve: se arregla haciendo el FONDO determinista, que es lo que hace la
 * utilidad `az-on-photo` del substrato (PAGE_CONTRACT §3.3.bis).
 */
function ColumnHeading({
  id,
  children,
  sobreFoto = false,
}: {
  id: string;
  children: string;
  sobreFoto?: boolean;
}) {
  return (
    <div className={styles.heading}>
      <h3 id={id} className={styles.headingTitle}>
        <span className={styles.rule} aria-hidden="true" />
        <span className={cx(styles.headingText, sobreFoto && styles.headingTextOnPhoto)}>{children}</span>
        <span className={styles.rule} aria-hidden="true" />
      </h3>
      {/* 12 y no 16: en el póster el corazón de la cabecera de columna mide
          ~13x9 px sobre 1024 (y 44..52). Con 16 (caja 16x14) la cabecera medía
          35,7 px contra los 26 del póster. */}
      <CorazonContorno className={styles.headingHeart} size={12} />
    </div>
  );
}

function Milestone({ day }: { day: ProgramDay }) {
  return (
    <li className={styles.milestone}>
      <span className={styles.medallion} aria-hidden="true">
        {day.day === 1 ? <IconSol /> : <IconLuna />}
      </span>
      <span className={styles.node} aria-hidden="true" />
      <div className={styles.milestoneBody}>
        <p className={styles.dayLabel}>{formatDayLabel(day)}</p>
        <p className={styles.dayDate}>
          <time dateTime={day.date}>{formatLongDate(day.date)}</time>
        </p>
        {/* EL TÍTULO DEL DÍA ESTABA EN EL DATO Y NO SE PINTABA. El programa del
         * cliente titula cada jornada («Sanación de la Consciencia y Frecuencia
         * Origen», «Recordar el Poder del Espíritu») y aquí sólo salía «DÍA 1» y
         * la fecha: se perdía justo lo que distingue un día del otro. */}
        <p className={styles.dayTitle}>{day.title}</p>
        <ul className={styles.blocks}>
          {day.blocks.map((block) => (
            <li key={block.id} className={styles.block}>
              <span className={styles.blockTime}>{formatBlockTime(block)}</span>
              <span className={styles.blockTitle}>{block.title}</span>
              {/* Y la descripción tampoco. En el bloque único del domingo es
               * literalmente lo que se compra: qué se activa en ese taller. */}
              {block.description ? <span className={styles.blockDesc}>{block.description}</span> : null}
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}

export function LowerGridBogota({ event }: { event: TourEvent }) {
  return (
    <Section background="surface" width="default" spacing="tight" className={styles.section}>
      <div className={styles.grid}>
        {/* --- Col 1 · CRONOGRAMA ---------------------------------------- */}
        <Reveal className={styles.column}>
          <section aria-labelledby="bogota-cronograma" className={styles.columnInner}>
            <ColumnHeading id="bogota-cronograma">Cronograma</ColumnHeading>
            <ol className={styles.timeline}>
              {event.program.map((day) => (
                <Milestone key={day.day} day={day} />
              ))}
            </ol>
          </section>
        </Reveal>

        {/* --- Col 2 · AZRAHEL -------------------------------------------- */}
        <Reveal className={styles.column} delay={0.08}>
          <section aria-labelledby="bogota-azrahel" className={styles.columnInner}>
            <ColumnHeading id="bogota-azrahel">Azrahel</ColumnHeading>
            <div className={styles.portraitRing}>
              <ResponsiveImage
                asset="azrahel/circulo"
                className={styles.portrait}
                sizes="(min-width: 64rem) 220px, 180px"
                alt="Azrahel del Mayor"
              />
            </div>
            <p className={styles.azrahelLead}>Guía, canal y semilla estelar.</p>
            <p className={styles.azrahelBody}>
              Acompaña procesos de despertar, sanación y recuerdo del Origen, uniendo sabiduría ancestral y
              frecuencias pleyadianas para activar tu propósito y tu luz.
            </p>
          </section>
        </Reveal>

        {/* --- Col 3 · ENTRADAS ------------------------------------------- */}
        <Reveal className={styles.column} delay={0.16}>
          <section aria-labelledby="bogota-entradas" className={styles.columnInner}>
            <div className={styles.ticketCard}>
              <ResponsiveImage
                asset="bogota/entradas-cielo"
                className={styles.ticketBg}
                sizes="(min-width: 64rem) 30vw, 100vw"
                decorative
              />
              <div className={styles.ticketBody}>
                <ColumnHeading id="bogota-entradas" sobreFoto>
                  Entradas
                </ColumnHeading>
                {/* Sin precio, sin aforo, sin escasez. El estado real lo pone
                    el dato: `salesState: 'coming-soon'`. */}
                <TicketCta event={event} variant="premium" size="lg" className={styles.ticketCta} />
                {/* O-13, replicado desde Buenos Aires. Va DEBAJO del botón a
                    propósito: el velo crema de esta tarjeta sólo es opaco en su
                    tramo final (0,94 al 100 %), así que arriba el texto caería
                    sobre el cielo de la foto y el contraste dependería del
                    píxel que toque. Abajo cae sobre crema y se mide una vez. */}
                <p className={styles.ticketLead}>
                  {formatDateRangeLong(event)}, en {event.city}. Nos vemos allí.
                </p>
                <p className={styles.ticketNota}>Compra en la tienda oficial de la gira.</p>
              </div>
            </div>
          </section>
        </Reveal>
      </div>
    </Section>
  );
}
